import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material'
import { ChevronDownIcon } from '@untitled-theme/icons-react'
import { useState } from 'react'

import type { AmortizationTabProps } from './AmortizationTab.types'
import DirectPurchaseChart from './DirectPurchaseChart'
import FinancingChart from './FinancingChart'
import MonthlyBurdenChart from './MonthlyBurdenChart'

type AccordionPanel = 'direktkauf' | 'finanzierung-einsparungen' | 'finanzierung-belastung'

const AmortizationTab = ({ retrofitEconomics, retrofitName, financing }: AmortizationTabProps) => {
  const [expanded, setExpanded] = useState<AccordionPanel | null>('direktkauf')
  const [selectedFinancingResult, setSelectedFinancingResult] = useState(
    financing?.financingResults[0] ?? null
  )

  const handleChange =
    (panel: AccordionPanel) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : null)
    }

  // Helper function to format amortization time
  const formatAmortization = (years: number | null | undefined) => {
    if (!years) return 'k. A.'
    return `${years} Jahre Amortisation`
  }

  return (
    <Box>
      {/* Direktkauf */}
      <Accordion
        expanded={expanded === 'direktkauf'}
        onChange={handleChange('direktkauf')}
        disableGutters
        elevation={0}
        sx={{
          '&:before': {
            display: 'none',
          },
          backgroundColor: 'transparent',
        }}
      >
        <AccordionSummary
          expandIcon={<ChevronDownIcon style={{ width: 24, height: 24, color: '#464A54' }} />}
          sx={{
            padding: 0,
            minHeight: '56px',
            '&.Mui-expanded': {
              minHeight: '56px',
            },
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
              '&.Mui-expanded': {
                margin: '16px 0',
              },
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '18px',
            }}
          >
            Direktkauf
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            padding: '0 0 16px 0',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                }}
              >
                {formatAmortization(retrofitEconomics.amortizationTimeInYears)}
              </Typography>
              <Typography
                sx={{
                  color: '#464A54',
                  fontWeight: 500,
                  fontSize: '12px',
                }}
              >
                Wann sind die Investitionskosten einer Wärmepumpe durch die eingesparten
                Energiekosten ausgeglichen?
              </Typography>
            </Box>

            {/* 18px spacing before chart */}
            {financing && (
              <Box sx={{ marginTop: '18px' }}>
                <DirectPurchaseChart financing={financing} retrofitName={retrofitName} />
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Horizontal Divider */}
      <Box
        sx={{
          height: '1px',
          backgroundColor: '#E1E1E4',
        }}
      />

      {/* Finanzierung – Einsparungen über 30 Jahre */}
      <Accordion
        expanded={expanded === 'finanzierung-einsparungen'}
        onChange={handleChange('finanzierung-einsparungen')}
        disableGutters
        elevation={0}
        sx={{
          '&:before': {
            display: 'none',
          },
          backgroundColor: 'transparent',
        }}
      >
        <AccordionSummary
          expandIcon={<ChevronDownIcon style={{ width: 24, height: 24, color: '#464A54' }} />}
          sx={{
            padding: 0,
            minHeight: '56px',
            '&.Mui-expanded': {
              minHeight: '56px',
            },
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
              '&.Mui-expanded': {
                margin: '16px 0',
              },
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '18px',
            }}
          >
            Finanzierung – Einsparungen über 30 Jahre
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            padding: '0 0 16px 0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Box
              sx={{
                padding: '16px',
                gap: '16px',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '8px',
                backgroundColor: '#FAFAFB',
                border: '1px solid #F5F5F7',
              }}
            >
              {/* Year Selector */}
              {financing && financing.financingResults.length > 0 ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      position: 'relative',
                      borderRadius: '12px',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        border: '2px solid #F5F5F7',
                        borderRadius: '12px',
                        pointerEvents: 'none',
                        zIndex: 0,
                      },
                    }}
                  >
                    {financing.financingResults.map(result => (
                      <Box
                        key={result.loanPeriod}
                        onClick={() => setSelectedFinancingResult(result)}
                        sx={{
                          flex: 1,
                          padding: '12px 16px',
                          textAlign: 'center',
                          fontWeight: 500,
                          fontSize: '14px',
                          lineHeight: '20px',
                          letterSpacing: '0.1px',
                          backgroundColor:
                            selectedFinancingResult?.loanPeriod === result.loanPeriod
                              ? '#181D29'
                              : 'transparent',
                          color:
                            selectedFinancingResult?.loanPeriod === result.loanPeriod
                              ? '#ffffff'
                              : undefined,
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          zIndex: 1,
                        }}
                      >
                        {result.loanPeriod} Jahre
                      </Box>
                    ))}
                  </Box>

                  {/* Content Box */}
                  {selectedFinancingResult && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      {/* Finanzierungssumme */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#464A54' }}>
                          Finanzierungssumme
                        </Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                          {(financing.netRetrofitCostsInCt / 100).toLocaleString('de-DE', {
                            style: 'currency',
                            currency: 'EUR',
                          })}
                        </Typography>
                      </Box>

                      {/* Zinsen */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#464A54' }}>
                          Zinsen
                        </Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                          {selectedFinancingResult.interestRateInPct}
                          {' %  p.a.'}
                        </Typography>
                      </Box>

                      {/* Divider */}
                      <Box
                        sx={{
                          height: '1px',
                          backgroundColor: '#E1E1E4',
                        }}
                      />

                      {/* Kreditrate */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#464A54' }}>
                          Kreditrate
                        </Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>
                          {(selectedFinancingResult.annuityInCt / 100 / 12).toLocaleString(
                            'de-DE',
                            {
                              style: 'currency',
                              currency: 'EUR',
                            }
                          )}{' '}
                          / Monat
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#74777F',
                  }}
                >
                  Keine Finanzierungsdaten verfügbar.
                </Typography>
              )}
            </Box>

            {selectedFinancingResult && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {/* Amortization Time */}
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                  }}
                >
                  {selectedFinancingResult.amortizationTime} Jahre Amortisation
                </Typography>

                {/* Energy Cost Description */}
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#74777F',
                  }}
                >
                  Energiekosten für{' '}
                  <Box component="span" sx={{ color: '#0E8C4A' }}>
                    saniert
                  </Box>{' '}
                  inkl.{' '}
                  <Box component="span" sx={{ color: '#A48105' }}>
                    Finanzierungkosten
                  </Box>{' '}
                  und{' '}
                  <Box component="span" sx={{ color: '#366192' }}>
                    unsaniert
                  </Box>{' '}
                  unter Beachtung der Energiekostenkostenprognose
                </Typography>
              </Box>
            )}

            {/* Financing Chart */}
            {financing && selectedFinancingResult && (
              <FinancingChart
                energyCostsResults={financing.energyCostTimeSeries}
                selectedFinancingResult={selectedFinancingResult}
              />
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Horizontal Divider */}
      <Box
        sx={{
          height: '1px',
          backgroundColor: '#E1E1E4',
        }}
      />

      {/* Finanzierung – monatliche Belastung */}
      <Accordion
        expanded={expanded === 'finanzierung-belastung'}
        onChange={handleChange('finanzierung-belastung')}
        disableGutters
        elevation={0}
        sx={{
          '&:before': {
            display: 'none',
          },
          backgroundColor: 'transparent',
        }}
      >
        <AccordionSummary
          expandIcon={<ChevronDownIcon style={{ width: 24, height: 24, color: '#464A54' }} />}
          sx={{
            padding: 0,
            minHeight: '56px',
            '&.Mui-expanded': {
              minHeight: '56px',
            },
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
              '&.Mui-expanded': {
                margin: '16px 0',
              },
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '18px',
            }}
          >
            Finanzierung – monatliche Belastung
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            padding: '0 0 16px 0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Box
              sx={{
                padding: '16px',
                gap: '16px',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '8px',
                backgroundColor: '#FAFAFB',
                border: '1px solid #F5F5F7',
              }}
            >
              {/* Year Selector */}
              {financing && financing.financingResults.length > 0 ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      position: 'relative',
                      borderRadius: '12px',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        border: '2px solid #F5F5F7',
                        borderRadius: '12px',
                        pointerEvents: 'none',
                        zIndex: 0,
                      },
                    }}
                  >
                    {financing.financingResults.map(result => (
                      <Box
                        key={result.loanPeriod}
                        onClick={() => setSelectedFinancingResult(result)}
                        sx={{
                          flex: 1,
                          padding: '12px 16px',
                          textAlign: 'center',
                          fontWeight: 500,
                          fontSize: '14px',
                          lineHeight: '20px',
                          letterSpacing: '0.1px',
                          backgroundColor:
                            selectedFinancingResult?.loanPeriod === result.loanPeriod
                              ? '#181D29'
                              : 'transparent',
                          color:
                            selectedFinancingResult?.loanPeriod === result.loanPeriod
                              ? '#ffffff'
                              : undefined,
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          zIndex: 1,
                        }}
                      >
                        {result.loanPeriod} Jahre
                      </Box>
                    ))}
                  </Box>

                  {/* Content Box */}
                  {selectedFinancingResult && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      {/* Finanzierungssumme */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#464A54' }}>
                          Finanzierungssumme
                        </Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                          {(financing.netRetrofitCostsInCt / 100).toLocaleString('de-DE', {
                            style: 'currency',
                            currency: 'EUR',
                          })}
                        </Typography>
                      </Box>

                      {/* Zinsen */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#464A54' }}>
                          Zinsen
                        </Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                          {selectedFinancingResult.interestRateInPct}%
                        </Typography>
                      </Box>

                      {/* Divider */}
                      <Box
                        sx={{
                          height: '1px',
                          backgroundColor: '#E1E1E4',
                        }}
                      />

                      {/* Kreditrate */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#464A54' }}>
                          Kreditrate
                        </Typography>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>
                          {(selectedFinancingResult.annuityInCt / 100 / 12).toLocaleString(
                            'de-DE',
                            {
                              style: 'currency',
                              currency: 'EUR',
                            }
                          )}{' '}
                          / Monat
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#74777F',
                  }}
                >
                  Keine Finanzierungsdaten verfügbar.
                </Typography>
              )}
            </Box>

            {selectedFinancingResult && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {/* Monthly Burden Examples */}
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                  }}
                >
                  Beispiele der effektiven monatliche Belastung
                </Typography>

                {/* Energy Cost Description */}
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#74777F',
                  }}
                >
                  Monatliche Energiekosten für{' '}
                  <Box component="span" sx={{ color: '#0E8C4A' }}>
                    saniert
                  </Box>{' '}
                  inkl.{' '}
                  <Box component="span" sx={{ color: '#A48105' }}>
                    Finanzierungkosten
                  </Box>{' '}
                  und{' '}
                  <Box component="span" sx={{ color: '#366192' }}>
                    unsaniert
                  </Box>{' '}
                  unter Beachtung der Energiekostenkostenprognose
                </Typography>
              </Box>
            )}

            {/* Monthly Burden Chart */}
            {financing && selectedFinancingResult && (
              <MonthlyBurdenChart
                energyCostsResults={financing.energyCostTimeSeries}
                selectedFinancingResult={selectedFinancingResult}
              />
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

export default AmortizationTab
